/**
 * BFF quick-fetch entrypoints — the BFF equivalent of chirp-quick-fetch.ts.
 *
 * IMPORTANT: This file imports the external `quick-fetch` package. It MUST NOT
 * be reachable from npm-shim/index.ts. It is only imported by chirp-bff.ts,
 * which is also outside the npm-shim import graph.
 */
import quickFetch from 'quick-fetch';
import { createBffRequestName, getBaseUrlForAuth, getInternalAuthToken } from './quick-fetch-utils';
import { deriveAuthFromDomain, getBffGateway } from './chirp-bff';
import { getGlobal } from './apiClients/global';

/**
 * Fires a BFF call via quick-fetch so the response is available before the
 * main app bundle finishes loading.
 *
 * Call this early in the page (e.g. in a `<script>` block or an eagerly-loaded
 * module) using the `<name>EarlyRequestDetails` const generated by client-types
 * under `bff/earlyRequest/`.  The later normal `client.call(...)` will
 * automatically pick up the in-flight or completed response.
 */
export const makeBffEarlyRequest = (details, inputs) => {
  var _location$hostname, _location;
  const auth = deriveAuthFromDomain((_location$hostname = (_location = getGlobal().location) === null || _location === void 0 ? void 0 : _location.hostname) !== null && _location$hostname !== void 0 ? _location$hostname : '');
  const {
    bffHash,
    name
  } = details;
  const {
    timeout,
    earlyRequestName
  } = inputs !== null && inputs !== void 0 ? inputs : {};
  const args = inputs === null || inputs === void 0 ? void 0 : inputs.args;
  const request = {
    args: [args !== null && args !== void 0 ? args : {}]
  };
  const clientTimeout = typeof timeout === 'number' ? timeout : 5000;
  const requestName = earlyRequestName !== null && earlyRequestName !== void 0 ? earlyRequestName : createBffRequestName(details, args);
  const gatewayPath = `${getBffGateway(auth)}/execute/hash/${bffHash}/${name}`;
  const baseUrl = getBaseUrlForAuth(auth);
  const url = quickFetch.getApiUrl(gatewayPath, undefined, undefined, baseUrl);
  const internalAuthToken = auth === 'internal' ? getInternalAuthToken() : null;
  const extraHeaders = internalAuthToken ? [{
    header: 'Authorization',
    value: `Bearer ${internalAuthToken}`
  }] : [];
  return new Promise((resolve, reject) => {
    quickFetch.makeEarlyRequest(requestName, {
      type: 'POST',
      data: JSON.stringify(request),
      extraHeaders,
      url,
      timeout: clientTimeout,
      noAuth: auth === 'internal',
      whenFinished: response => {
        try {
          resolve(JSON.parse(response));
        } catch (e) {
          reject(e);
        }
      },
      onError: (_xhr, errorMessage) => {
        reject(new Error(errorMessage));
      }
    });
  });
};

/**
 * Returns the deterministic quick-fetch request name that
 * `makeBffEarlyRequest` / `getBffEarlyRequest` would use for the given
 * details + inputs. Useful for embedding the name in the page HTML so it
 * can be looked up before calling `makeBffEarlyRequest`.
 */
export const getBffEarlyRequestName = (details, inputs) => {
  const args = inputs === null || inputs === void 0 ? void 0 : inputs.args;
  return createBffRequestName(details, args);
};

/**
 * Checks whether a BFF early request is in-flight or has completed.
 * If so, returns a Promise that resolves to the parsed response (and evicts
 * the entry from quick-fetch so it is consumed exactly once). Returns
 * `undefined` if no matching entry exists.
 *
 * This is called internally by `createBffClient` — consumers do not need to
 * call it directly.
 */
export const getBffEarlyRequest = ({
  details,
  inputs,
  name
}) => {
  const args = inputs === null || inputs === void 0 ? void 0 : inputs.args;
  const requestName = name !== null && name !== void 0 ? name : createBffRequestName(details, args);
  const requestState = quickFetch.getRequestStateByName(requestName);
  if (requestState && !requestState.error) {
    return new Promise((resolve, reject) => {
      requestState.whenFinished(response => {
        quickFetch.removeEarlyRequest(requestName);
        try {
          resolve(JSON.parse(response));
        } catch (e) {
          reject(e);
        }
      });
      requestState.onError((_xhr, errorMessage) => {
        quickFetch.removeEarlyRequest(requestName);
        reject(new Error(errorMessage));
      });
    });
  }
  return undefined;
};